/**
 * PATH: apps/quote/src/pages/TemplateDetailPage.tsx
 * PURPOSE: View and edit a single Spreadsheet Template
 *
 * WHY: Allows users to:
 *   - View template details and bindings
 *   - Update template workbook
 *   - Manage named range bindings
 *   - Preview spreadsheet structure
 */

import { useEffect, useState, useCallback } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  FileSpreadsheet,
  Upload,
  Save,
  Trash2,
  CheckCircle2,
  Clock,
  Archive,
  Loader2,
  AlertCircle,
  Edit2,
  Table,
  Tag,
  Hash,
  Type,
} from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'
import { quoteApi, type SpreadsheetTemplateDetail, type NamedRangeBinding } from '@/lib/quoteApi'
import { useQuoteTabStore } from '../config/tabs'

// =============================================================================
// Types
// =============================================================================

const DIRECTION_LABELS: Record<string, { label: string; color: string }> = {
  input: { label: 'Input', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  output: { label: 'Output', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  bidirectional: { label: 'Bi-directional', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  computed: { label: 'Computed', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
}

const DATA_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  text: Type,
  number: Hash,
  currency: Tag,
  percent: Hash,
  date: Clock,
  boolean: CheckCircle2,
}

// =============================================================================
// Components
// =============================================================================

function BindingCard({ binding }: { binding: NamedRangeBinding }) {
  const DirectionInfo = DIRECTION_LABELS[binding.direction] || DIRECTION_LABELS.input
  const DataTypeIcon = DATA_TYPE_ICONS[binding.data_type] || Type

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
            <Table className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white text-sm">{binding.name}</h4>
            <code className="text-xs text-slate-500 dark:text-slate-400">{binding.range_address || 'No address'}</code>
          </div>
        </div>
        <span className={cn('text-xs px-2 py-1 rounded-full font-medium', DirectionInfo.color)}>
          {DirectionInfo.label}
        </span>
      </div>

      {binding.description && (
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{binding.description}</p>
      )}

      <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1">
          <DataTypeIcon className="h-3 w-3" />
          {binding.data_type}
        </span>
        {binding.domain_path && (
          <span className="flex items-center gap-1">
            → {binding.domain_path}
          </span>
        )}
        {binding.required && (
          <span className="text-red-500 dark:text-red-400">Required</span>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function TemplateDetailPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateActiveTab = useQuoteTabStore(s => s.updateActiveTab)

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // Fetch template details
  const { data: template, isLoading, error } = useQuery({
    queryKey: ['spreadsheet-template', templateId],
    queryFn: () => quoteApi.getTemplate(templateId!),
    enabled: !!templateId,
    onSuccess: (data) => {
      setEditName(data.name)
      setEditDescription(data.description || '')
    },
  })

  useEffect(() => {
    if (template?.name) {
      updateActiveTab(location.pathname, template.name)
    }
  }, [template?.name, location.pathname, updateActiveTab])

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      quoteApi.updateTemplate(templateId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spreadsheet-template', templateId] })
      setIsEditing(false)
    },
  })

  // Upload workbook mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => quoteApi.uploadTemplateWorkbook(templateId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spreadsheet-template', templateId] })
    },
  })

  // Activate/Archive mutations
  const activateMutation = useMutation({
    mutationFn: () => quoteApi.activateTemplate(templateId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['spreadsheet-template', templateId] }),
  })

  const archiveMutation = useMutation({
    mutationFn: () => quoteApi.archiveTemplate(templateId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['spreadsheet-template', templateId] }),
  })

  const handleSave = useCallback(() => {
    updateMutation.mutate({ name: editName, description: editDescription })
  }, [editName, editDescription, updateMutation])

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadMutation.mutate(file)
    }
  }, [uploadMutation])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error || !template) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Template not found</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {(error as Error)?.message || 'The template you are looking for does not exist'}
        </p>
        <button
          onClick={() => navigate('/templates')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Templates
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/templates')}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-2xl font-bold bg-transparent border-b-2 border-blue-500 focus:outline-none text-slate-900 dark:text-white"
              />
            ) : (
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{template.name}</h1>
            )}
            <div className="flex items-center gap-3 mt-1">
              <span className={cn(
                'text-xs px-2 py-0.5 rounded-full font-medium capitalize',
                template.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                template.status === 'draft' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              )}>
                {template.status}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400 capitalize">
                {template.category}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                v{template.version}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
                {template.status === 'draft' && (
                  <button
                    onClick={() => activateMutation.mutate()}
                    disabled={activateMutation.isPending}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    {activateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    <CheckCircle2 className="h-4 w-4" />
                    Activate
                  </button>
                )}
                {template.status === 'active' && (
                  <button
                    onClick={() => archiveMutation.mutate()}
                    disabled={archiveMutation.isPending}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                  >
                    {archiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Archive className="h-4 w-4" />
                    Archive
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Description</h2>
              {isEditing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  placeholder="Describe the template..."
                />
              ) : (
                <p className="text-slate-600 dark:text-slate-400">
                  {template.description || 'No description provided'}
                </p>
              )}
            </div>

            {/* Bindings */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900 dark:text-white">
                  Named Range Bindings ({template.bindings?.length || 0})
                </h2>
              </div>
              
              {template.bindings && template.bindings.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {template.bindings.map((binding) => (
                    <BindingCard key={binding.name} binding={binding} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Table className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No bindings defined</p>
                  <p className="text-sm mt-1">Upload an Excel file with named ranges to auto-detect bindings</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upload Workbook */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Workbook</h2>
              
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center">
                <label className="cursor-pointer">
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {uploadMutation.isPending ? 'Uploading...' : 'Drop Excel file or click to upload'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500">
                    .xlsx or .xls files
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={uploadMutation.isPending}
                    className="hidden"
                  />
                </label>
              </div>
              
              {uploadMutation.isSuccess && (
                <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg text-sm text-green-700 dark:text-green-400">
                  ✓ Workbook uploaded successfully
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Stats</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">Times Used</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">{template.times_used}x</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">Bindings</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">{template.binding_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">Version</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">v{template.version}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">Created</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {template.created_at ? formatDate(template.created_at) : 'Unknown'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">Updated</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {template.updated_at ? formatDate(template.updated_at) : 'Never'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
